import request from 'axios';
import without from 'lodash/without';
import omit from 'lodash/omit';
import { createSelector } from 'reselect';
import { combineReducers } from 'redux';
import { PAGINATION_PAGE_SIZE } from 'common/variables';
import { getPaginationValues, getFiltersString, getFormattedFilters } from 'common/utils';
import portfolioEntryReducer from 'profile/ducks/portfolio_entry';
import portfolioFormReducer from 'profile/ducks/portfolio_form';

/* TODO: remove this once we move portfolios to the new gallery components */
const FILTERS = [[{
  field: "category_id",
  label: "category",
  options: [
    [7, "Logo & Identity"],
    [8, "Naming & Branding"],
    [9, "Product & Packaging"],
    [10, "Web & Mobile Design"],
    [5, "Business & Advertising"],
    [2, "Art & Illustration"],
    [6, "Clothing & Merchandise"],
    [3, "Book & Magazine"],
    [11, "Other"]
  ]
}]];


/* ACTIONS
================================================================================================ */
const FETCH = 'crowdspring/portfolio/FETCH_PORTFOLIOS';
const FETCH_REQUEST = 'crowdspring/portfolio/FETCH_PORTFOLIOS_REQUEST';
const FETCH_SUCCESS = 'crowdspring/portfolio/FETCH_PORTFOLIOS_SUCCESS';
const FETCH_FAILURE = 'crowdspring/portfolio/FETCH_PORTFOLIOS_FAILURE';
const FETCH_ALL = 'crowdspring/portfolio/FETCH_ALL_PORTFOLIOS';
const FETCH_ALL_REQUEST = 'crowdspring/portfolio/FETCH_ALL_PORTFOLIOS_REQUEST';
const FETCH_ALL_SUCCESS = 'crowdspring/portfolio/FETCH_ALL_PORTFOLIOS_SUCCESS';
const FETCH_ALL_FAILURE = 'crowdspring/portfolio/FETCH_ALL_PORTFOLIOS_FAILURE';
// const FETCH_ENTRY = 'crowdspring/portfolio/FETCH_PORTFOLIO_ENTRY';
const FETCH_ENTRY_REQUEST = 'crowdspring/portfolio/FETCH_PORTFOLIO_ENTRY_REQUEST';
const FETCH_ENTRY_SUCCESS = 'crowdspring/portfolio/FETCH_PORTFOLIO_ENTRY_SUCCESS';
const FETCH_ENTRY_FAILURE = 'crowdspring/portfolio/FETCH_PORTFOLIO_ENTRY_FAILURE';
const LOAD_ENTRIES_PAGE = 'crowdspring/portfolio/LOAD_ENTRIES_PAGE';
// const CREATE = 'crowdspring/portfolio/CREATE_PORTFOLIO';
// const DELETE = 'crowdspring/portfolio/DELETE_PORTFOLIO';
const DELETE_REQUEST = 'crowdspring/portfolio/DELETE_PORTFOLIO_REQUEST';
const DELETE_SUCCESS = 'crowdspring/portfolio/DELETE_PORTFOLIO_SUCCESS';
const DELETE_FAILURE = 'crowdspring/portfolio/DELETE_PORTFOLIO_FAILURE';
const CREATE_REQUEST = 'crowdspring/portfolio/CREATE_PORTFOLIO_REQUEST';
const CREATE_SUCCESS = 'crowdspring/portfolio/CREATE_PORTFOLIO_SUCCESS';
const CREATE_FAILURE = 'crowdspring/portfolio/CREATE_PORTFOLIO_FAILURE';
const SAVE = 'crowdspring/portfolio/SAVE_PORTFOLIO';
const SAVE_REQUEST = 'crowdspring/portfolio/SAVE_PORTFOLIO_REQUEST';
const SAVE_SUCCESS = 'crowdspring/portfolio/SAVE_PORTFOLIO_SUCCESS';
const SAVE_FAILURE = 'crowdspring/portfolio/SAVE_PORTFOLIO_FAILURE';
// const SAVE_SORTING = 'crowdspring/portfolio/SAVE_SORTING';
const SAVE_SORTING_REQUEST = 'crowdspring/portfolio/SAVE_SORTING_REQUEST';
const SAVE_SORTING_SUCCESS = 'crowdspring/portfolio/SAVE_SORTING_SUCCESS';
const SAVE_SORTING_FAILURE = 'crowdspring/portfolio/SAVE_SORTING_FAILURE';

const SET_PORTFOLIO_DIRTY = 'crowdspring/portfolio/SET_PORTFOLIO_DIRTY';
const SET_PORTFOLIO_CLEAN = 'crowdspring/portfolio/SET_PORTFOLIO_CLEAN';
const SAVE_FILTERS = 'crowdspring/portfolio/SAVE_PORTFOLIO_FILTERS';
const CLEAR_FILTERS = 'crowdspring/portfolio/CLEAR_PORTFOLIO_FILTERS';
const TOGGLE_EDIT_MODE = 'crowdspring/portfolio/TOGGLE_EDIT_MODE';
const TOGGLE_DETAILS_MODE = 'crowdspring/portfolio/TOGGLE_DETAILS_MODE';


/* INITIAL STATES
================================================================================================ */
export const INITIAL_STATE = {
  isFetching: false,
  isError: false,
  entriesById: {},
  visibleEntries: [],
  pageSize: null,
  itemsCount: null,
  pageCount: null,
  currentPage: null,
  pagesLimits: {},
  appliedFilters: {},
  editMode: false,
  detailsMode: false,
  portfolioDirty: false,
  reorderedEntries: null,
  profileUsername: ''
};


/* HELPERS
================================================================================================ */
export const getPortfolio = state => state.profile.portfolio.portfolioPage;
export const getPortfolioFilters = () => FILTERS;
export const getAppliedPortfolioFilters = (state, formName) => (
  getFormattedFilters(getPortfolio(state).appliedFilters, formName)
);

const shouldFetchPortfolioEntries = (state, username) => {
  const { visibleEntries, profileUsername, isFetching } = getPortfolio(state);

  if (((!visibleEntries.length || username !== profileUsername) && !isFetching)) {
    return true;
  }
  return false;
};

export const getVisibleEntries = createSelector(
  [getPortfolio],
  (portfolioState) => {
    const visibleEntries = portfolioState.visibleEntries;
    const entriesById = portfolioState.entriesById;

    return visibleEntries.map(entry => (
      entriesById[entry]
    ));
  }
);


/* REDUCERS
================================================================================================ */
const pageReducer = (state = INITIAL_STATE, action) => {
  if (!action) {
    return state;
  }

  let visibleEntries;
  let entries;
  let data;
  let pagesLimits;

  switch (action.type) {
    case DELETE_REQUEST:
    case CREATE_REQUEST:
    case SAVE_REQUEST:
    case SAVE_SORTING_REQUEST:
    case FETCH_ENTRY_REQUEST:
    case FETCH_ALL_REQUEST:
    case FETCH_REQUEST:
      return {
        ...state,
        isFetching: true,
      };
    case DELETE_SUCCESS:
      const entryId = action.payload;
      const entry = state.entriesById[entryId];
      visibleEntries = without(state.visibleEntries, entryId);
      entries = omit(state.entriesById, [entryId]);

      for (const v of Object.values(entries)) {
        if (v.sort_order > entry.sort_order) {
          entries[v.id] = { ...v, sort_order: v.sort_order - 1 };
        }
      }

      return {
        ...state,
        isFetching: false,
        isError: false,
        entriesById: entries,
        visibleEntries,
      };
    case CREATE_SUCCESS:
      data = action.payload;
      entries = { ...state.entriesById };
      visibleEntries = [...state.visibleEntries];
      visibleEntries.pop();
      visibleEntries.push(data.id);
      entries[data.id] = { ...data };

      return {
        ...state,
        isFetching: false,
        isError: false,
        visibleEntries,
        entriesById: { ...entries },
      };
    case SAVE_SUCCESS:
      data = action.payload;
      entries = { ...state.entriesById };
      visibleEntries = [...state.visibleEntries];
      entries[data.id] = { ...data };
      visibleEntries.push(data.id);

      return {
        ...state,
        isFetching: false,
        isError: false,
        entriesById: { ...entries },
        visibleEntries
      };
    case SAVE_SORTING_SUCCESS:
      visibleEntries = [];

      for (const [k, v] of Object.entries(state.reorderedEntries)) {
        visibleEntries[v] = k;
      }

      return {
        ...state,
        isFetching: false,
        isError: false,
        visibleEntries,
      };

    case FETCH_ENTRY_SUCCESS:
      data = action.payload;
      entries = {};

      data.results.forEach((entry) => {
        entries[entry.id] = { ...entry };
      });

      return {
        ...state,
        isFetching: false,
        isError: false,
        entriesById: { ...state.entriesById, ...entries },
      };
    case FETCH_SUCCESS:
      data = action.payload;
      const { currentPage, pageCount } = getPaginationValues(data);
      const pagesData = { ...state.pagesLimits };
      pagesLimits = {
        nextPage: data.next,
        prevPage: data.previous || 0,
      };
      entries = {};
      visibleEntries = [];

      data.results.forEach((entry) => {
        entries[entry.id] = { ...entry };
        visibleEntries.push(entry.id);
      });

      if (!pagesData[currentPage]) {
        pagesData[currentPage] = { ...pagesLimits };
      }

      return {
        ...state,
        isFetching: false,
        isError: false,
        entriesById: { ...state.entriesById, ...entries },
        itemsCount: data.count,
        pageSize: data.limit,
        pagesLimits: { ...pagesData },
        visibleEntries,
        pageCount,
        currentPage,
        profileUsername: action.profileUsername
      };
    case FETCH_ALL_SUCCESS:
      data = action.payload;
      entries = {};
      visibleEntries = [];

      data.forEach((entry) => {
        if (!entries[entry.id]) {
          entries[entry.id] = { ...entry };
        }
        visibleEntries.push(entry.id);
      });

      return {
        ...state,
        isFetching: false,
        isError: false,
        entriesById: { ...state.entriesById, ...entries },
        visibleEntries,
      };
    case LOAD_ENTRIES_PAGE:
      const page = action.payload;
      visibleEntries = [...state.visibleEntries];
      pagesLimits = state.pagesLimits[page];
      const newVisibleEntries = visibleEntries.slice(
        pagesLimits.prevPage,
        pagesLimits.prevPage + state.pageSize
      );

      return {
        ...state,
        isFetching: false,
        isError: false,
        visibleEntries: [...newVisibleEntries],
        currentPage: page,
      };
    case SET_PORTFOLIO_DIRTY:
      data = action.payload;

      return {
        ...state,
        reorderedEntries: { ...data },
        portfolioDirty: true,
      };
    case SET_PORTFOLIO_CLEAN:
      return {
        ...state,
        reorderedEntries: null,
        portfolioDirty: false,
      };
    case SAVE_FILTERS:
      return {
        ...state,
        appliedFilters: { ...action.payload },
      };
    case CLEAR_FILTERS:
      return {
        ...state,
        appliedFilters: {},
      };
    case TOGGLE_EDIT_MODE:
      return {
        ...state,
        editMode: !state.editMode,
      };
    case TOGGLE_DETAILS_MODE:
      return {
        ...state,
        detailsMode: !state.detailsMode,
      };

    // Failed requests
    case DELETE_FAILURE:
    case CREATE_FAILURE:
    case SAVE_FAILURE:
    case SAVE_SORTING_FAILURE:
    case FETCH_ENTRY_FAILURE:
    case FETCH_ALL_FAILURE:
    case FETCH_FAILURE:
      return {
        ...state,
        isFetching: false,
        isError: true,
      };
    default:
      return state;
  }
};

const reducer = combineReducers({
  portfolioPage: pageReducer,
  portfolioEntry: portfolioEntryReducer,
  portfolioForm: portfolioFormReducer,
});
export default reducer;


/* ACTION CREATORS
================================================================================================ */
export const updatePortfolioEntry = (entryData, username, portfolioId) => ({
  type: SAVE,
  promise: request.patch(`/api/v1/users/${username}/portfolio_images/${portfolioId}/`, { ...entryData }),
});

export const createPortfolioByUpload = (entryData, username) => ({
  type: SAVE,
  promise: request.post(`/api/v1/users/${username}/portfolio_images/`, { ...entryData }),
});

const createPortfolioSuccess = response => (dispatch) => {
  dispatch({
    type: CREATE_SUCCESS,
    payload: response.data,
  });

  return Promise.resolve(response.data);
};

export const createPortfolioByRevision = (entryData, username) => (dispatch) => {
  dispatch({ type: CREATE_REQUEST });

  return request.post(`/api/v1/users/${username}/portfolio_images/`, { ...entryData })
    .then(resp => dispatch(createPortfolioSuccess(resp)));
};

const fetchEntrySuccess = response => (dispatch) => {
  dispatch({
    type: FETCH_ENTRY_SUCCESS,
    payload: response.data,
  });

  return Promise.resolve(response.data.results[0].id);
};

const fetchEntryFailure = error => (dispatch) => {
  dispatch({
    type: FETCH_ENTRY_FAILURE,
    payload: error,
  });

  return Promise.reject(error);
};

export const fetchPortfolioEntry = (profileUsername, offset) => (dispatch, getState) => {
  const o = offset || 0;
  const filtersStr = getFiltersString(getPortfolio(getState()).appliedFilters);

  dispatch({
    type: FETCH_ENTRY_REQUEST,
  });

  return request.get(`/api/v1/users/${profileUsername}/portfolio_images/?limit=1&offset=${o}${filtersStr}`)
  .then(resp => dispatch(fetchEntrySuccess(resp)))
  .catch(error => dispatch(fetchEntryFailure(error)));
};

export const fetchPortfolioEntries = (profileUsername, offset) => (dispatch, getState) => {
  const o = offset || 0;
  const filtersStr = getFiltersString(getPortfolio(getState()).appliedFilters);

  return dispatch({
    type: FETCH,
    profileUsername: profileUsername,
    promise: request.get(`/api/v1/users/${profileUsername}/portfolio_images/?limit=${PAGINATION_PAGE_SIZE}&offset=${o}${filtersStr}`),
  });
};

export const fetchAllPortfolioEntries = profileUsername => ({
  type: FETCH_ALL,
  promise: request.get(`/api/v1/users/${profileUsername}/portfolio_images/all/`),
});

export const savePortfolioFilters = filters => (dispatch) => {
  dispatch({
    type: SAVE_FILTERS,
    payload: filters,
  });

  return Promise.resolve(true);
};

const savePortfolioSorting = (orderObject, profileUsername) => (
  request.post(`/api/v1/users/${profileUsername}/portfolio_images/order/`, { order: orderObject })
);

export const deletePortfolio = (profileUsername, portfolioId) => (dispatch) => {
  dispatch({ type: DELETE_REQUEST });

  return request.delete(`/api/v1/users/${profileUsername}/portfolio_images/${portfolioId}/`)
    .then(() => (
      dispatch({ type: DELETE_SUCCESS, payload: portfolioId })
    ))
    .catch(err => (
      dispatch({ type: DELETE_FAILURE, payload: err })
    ));
};

export const fetchPortfolioEntriesIfNeeded = (profileUsername) => (dispatch, getState) => {
  const state = getState();

  if (shouldFetchPortfolioEntries(state, profileUsername)) {
    return dispatch(fetchPortfolioEntries(profileUsername));
  }

  return Promise.resolve(true);
};

export const setPortfolioDirty = entriesList => (dispatch) => {
  const entriesIds = {};

  entriesList.forEach((entry) => {
    entriesIds[entry.id] = entry.sort_order;
  });

  dispatch({
    type: SET_PORTFOLIO_DIRTY,
    payload: entriesIds,
  });
};

export const toggleEditMode = profileUsername => (dispatch, getState) => {
  const state = getState();
  const portfolio = getPortfolio(state);

  dispatch({
    type: TOGGLE_EDIT_MODE,
  });

  if (!portfolio.editMode) { // if not editMode
    return dispatch(fetchAllPortfolioEntries(profileUsername));
  } else if (portfolio.editMode && portfolio.portfolioDirty) { // if editMode
    // save portfolio ordering
    dispatch({
      type: SAVE_SORTING_REQUEST,
    });

    return savePortfolioSorting(portfolio.reorderedEntries, profileUsername)
    .then(() => {
      dispatch({
        type: SAVE_SORTING_SUCCESS,
      });
      dispatch({
        type: SET_PORTFOLIO_CLEAN,
      });

      return dispatch({ type: LOAD_ENTRIES_PAGE, payload: 1 });
    });
  }

  return dispatch({ type: LOAD_ENTRIES_PAGE, payload: 1 });
};

export const toggleDetailsMode = () => (
  {
    type: TOGGLE_DETAILS_MODE,
  }
);
